/**
 * Accessibility utilities for better screen reader support and keyboard navigation
 */

export const ARIA_LABELS = {
  // Navigation
  mainNavigation: "Main navigation",
  skipToContent: "Skip to main content",
  backToTop: "Back to top",

  // Interactive elements
  toggleKonamiMode: "Toggle TRON visual mode",
  openExternalLink: "Opens in new tab",
  closeModal: "Close modal",

  // Loading states
  loading: "Content is loading",
  loadingComplete: "Content loaded successfully",

  // Form elements
  searchInput: "Search repositories",
  submitForm: "Submit form",

  // Content sections
  projectsList: "List of projects",
  techStack: "Technology stack",
  contactInfo: "Contact information",
} as const;

export const SCREEN_READER_ONLY =
  "sr-only absolute -inset-px h-px w-px overflow-hidden whitespace-nowrap border-0 p-0";

/**
 * Creates accessible button props for interactive elements
 */
export const createAccessibleButton = (label: string, description?: string) => ({
  "aria-label": label,
  "aria-describedby": description,
  role: "button",
  tabIndex: 0,
});

/**
 * Creates accessible link props for external links
 */
export const createExternalLinkProps = (url: string, label: string) => ({
  href: url,
  target: "_blank",
  rel: "noopener noreferrer",
  "aria-label": `${label} ${ARIA_LABELS.openExternalLink}`,
});

/**
 * Creates accessible modal props
 */
export const createModalProps = (isOpen: boolean, title: string) => ({
  role: "dialog",
  "aria-modal": true,
  "aria-labelledby": `${title.toLowerCase().replace(/\s+/g, "-")}-title`,
  "aria-hidden": !isOpen,
});

/**
 * Creates accessible list props
 */
export const createListProps = (label: string, itemCount?: number) => ({
  role: "list",
  "aria-label": label,
  "aria-live": "polite",
  ...(itemCount && { "aria-describedby": `${itemCount} items` }),
});

/**
 * Announces content changes to screen readers
 */
export const announceToScreenReader = (
  message: string,
  priority: "polite" | "assertive" = "polite"
): void => {
  const announcement = document.createElement("div");
  announcement.setAttribute("aria-live", priority);
  announcement.setAttribute("aria-atomic", "true");
  announcement.className = SCREEN_READER_ONLY;
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Focus management utilities
 */
export const focusManagement = {
  /**
   * Trap focus within a container
   */
  trapFocus: (container: HTMLElement): (() => void) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    container.addEventListener("keydown", handleTabKey);
    firstElement?.focus();

    // Return cleanup function
    return () => {
      container.removeEventListener("keydown", handleTabKey);
    };
  },

  /**
   * Restore focus to previously focused element
   */
  restoreFocus: (previousElement: HTMLElement | null): void => {
    if (previousElement && document.contains(previousElement)) {
      previousElement.focus();
    }
  },
};

/**
 * Keyboard navigation utilities
 */
export const keyboardNavigation = {
  /**
   * Handle arrow key navigation in a list
   */
  handleArrowKeys: (
    event: KeyboardEvent,
    items: HTMLElement[],
    currentIndex: number,
    onIndexChange: (index: number) => void
  ): void => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        onIndexChange(currentIndex < items.length - 1 ? currentIndex + 1 : 0);
        break;
      case "ArrowUp":
        event.preventDefault();
        onIndexChange(currentIndex > 0 ? currentIndex - 1 : items.length - 1);
        break;
      case "Home":
        event.preventDefault();
        onIndexChange(0);
        break;
      case "End":
        event.preventDefault();
        onIndexChange(items.length - 1);
        break;
    }
  },
};
